import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';

// Las columnas de preferencias son de texto; la función de la base espera texto.
const aTexto = (valor) => (valor === null || valor === undefined ? null : String(valor));

const useUserPreferences = (userEmail) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);

  // Cargar datos del usuario desde Supabase
  const loadUserData = useCallback(async () => {
    if (!userEmail) return null;
    
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Cargando datos del usuario:', userEmail);

      const { data, error } = await supabase.rpc('perfil_portal', { p_correo: userEmail });

      if (error) throw error;
      if (!data) throw new Error('Usuario no encontrado en la base de datos');

      console.log('✅ Datos del usuario cargados:', data);
      setUserData(data);
      return data;
    } catch (err) {
      console.error('❌ Error cargando datos del usuario:', err);
      setError(err.message || 'Error al cargar los datos del usuario');
      return null;
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  // Cargar datos del usuario al inicializar
  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  // Guardar preferencias del usuario - ACTUALIZADO CON flag_sunat
  const savePreferences = useCallback(async (preferences) => {
    try {
      setLoading(true);
      setError(null);

      console.log('💾 Guardando preferencias para:', userEmail, preferences);

      // La función solo puede tocar estas seis columnas: ni plan, ni fechas, ni importes.
      const argumentos = {
        p_correo: userEmail,
        p_flag_correo: aTexto(preferences.flag_correo),
        p_tipo: aTexto(preferences.tipo),
        p_palabras_claves: aTexto(preferences.palabras_claves),
        p_departamentos: aTexto(preferences.departamentos),
        p_flag_sunat: aTexto(preferences.flag_sunat),
        p_hora_envio: aTexto(preferences.hora_envio),
        p_flag_seace: aTexto(preferences.flag_seace)
      };

      let parcial = false; // true = la base no aceptó las preferencias por tipo de proceso
      let { data, error } = await supabase.rpc('guardar_preferencias_portal', argumentos);

      // Si la migración todavía no está aplicada, la función con las preferencias por
      // tipo de proceso no existe: se guarda el resto y esas dos quedan para después.
      if (error && (error.code === 'PGRST202' || /hora_envio|flag_seace|does not exist|no existe/i.test(error.message || ''))) {
        console.warn('⚠️ La base aún no acepta hora_envio / flag_seace; se guardan las demás preferencias.');
        const { p_hora_envio, p_flag_seace, ...soloLasViejas } = argumentos;
        ({ data, error } = await supabase.rpc('guardar_preferencias_portal', soloLasViejas));
        parcial = !error;
      }

      if (error) throw error;
      if (!data) throw new Error('Usuario no encontrado en la base de datos');

      console.log('✅ Preferencias guardadas exitosamente:', data);
      
      // El estado local refleja lo que el usuario acaba de elegir: si la base devolvió
      // menos columnas de las enviadas, la pantalla no debe revertirse sola.
      setUserData(prev => ({ ...prev, ...preferences, ...data }));
      
      return { success: true, data, parcial };
    } catch (err) {
      console.error('❌ Error guardando preferencias:', err);
      setError(err.message || 'Error al guardar las preferencias');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  // Calcular días restantes
  const calcularDiasRestantes = useCallback((fechaFin) => {
    if (!fechaFin) return 0;
    const hoy = new Date();
    const fin = new Date(fechaFin);
    const diffTime = fin - hoy;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }, []);

  // Obtener días restantes
  const getDiasRestantes = useCallback(() => {
    if (!userData) return 0;
    
    if (userData.dias_restantes !== undefined && userData.dias_restantes !== null) {
      return userData.dias_restantes;
    }
    
    return calcularDiasRestantes(userData.fecha_fin);
  }, [userData, calcularDiasRestantes]);

  // Obtener datos formateados - ACTUALIZADO
  const getFormattedData = useCallback(() => {
    if (!userData) return null;

    return {
      // Datos básicos del usuario
      nombre: userData.nombre || '',
      correo: userData.correo || '',
      empresa: userData.empresa || '',
      celular: userData.celular || '',
      ruc: userData.ruc || '',
      plan: userData.plan || 'MENOR',
      // Grupos del plan Curso: de ellos depende qué secciones ve el alumno.
      cursoTipo: userData.curso_tipo || '',
      estado: userData.estado || 'vigente',
      
      // Días restantes
      diasRestantes: getDiasRestantes(),
      
      // Preferencias (con valores por defecto)
      flagCorreo: userData.flag_correo ? userData.flag_correo === 'SI' : true,
      tipos: userData.tipo || '',
      palabrasClaves: userData.palabras_claves || '',
      departamentos: userData.departamentos || '',
      busquedaSimilar: userData.flag_sunat || 'NO', // ✅ AGREGADO
      horaEnvio: userData.hora_envio === undefined || userData.hora_envio === null
        ? '22'
        : String(userData.hora_envio),
      flagSeace: userData.flag_seace === undefined || userData.flag_seace === null
        ? 'SI'
        : String(userData.flag_seace),
      
      // Datos adicionales
      fechaInicio: userData.fecha_inicio,
      fechaFin: userData.fecha_fin,
      valor: userData.valor,
      mesSuscripcion: userData.mes_suscripcion
    };
  }, [userData, getDiasRestantes]);

  // Función para actualizar solo las preferencias localmente
  const updateLocalPreferences = useCallback((newPreferences) => {
    if (!userData) return;
    
    setUserData(prevData => ({
      ...prevData,
      flag_correo: newPreferences.flag_correo || prevData.flag_correo,
      tipo: newPreferences.tipo || prevData.tipo,
      palabras_claves: newPreferences.palabras_claves || prevData.palabras_claves,
      departamentos: newPreferences.departamentos || prevData.departamentos,
      flag_sunat: newPreferences.flag_sunat || prevData.flag_sunat, // ✅ AGREGADO
      // La hora sí puede quedar vacía ('' = no recibe el correo de menores).
      hora_envio: newPreferences.hora_envio === undefined
        ? prevData.hora_envio
        : newPreferences.hora_envio,
      flag_seace: newPreferences.flag_seace || prevData.flag_seace
    }));
  }, [userData]);

  return {
    userData,
    formattedData: getFormattedData(),
    loading,
    error,
    loadUserData,
    savePreferences,
    updateLocalPreferences,
    getDiasRestantes
  };
};

export { useUserPreferences };
export default useUserPreferences;